"use client";

import { useState } from "react";
import Link from "next/link";
import SectionHeading from "./section-heading";
import { ArrowUpRight } from "lucide-react";

type TabKey = "inventory" | "global";

const tabs = [
  {
    key: "inventory" as const,
    label: "Inventory Management",
    heading: "Inventory Management",
    text: "ISO-certified warehouses provide safety stock, FIFO control, batch traceability, and customer-defined release protocols. Inventory records include material status, inspection disposition, and dispatch documentation.",
    imgSrc: "/homepage/4.jpeg",
  },
  {
    key: "global" as const,
    label: "Global Operations",
    heading: "Global Operations",
    text: "Supply management networks across India, China, Vietnam, and South Korea support supplier qualification, audits, production tracking, and logistics. Deliveries are executed to customer or third-party locations as defined in the purchase order.",
    imgSrc: "/homepage/quality.png",
  },
];

export default function Services() {
  const [active, setActive] = useState<TabKey>("inventory");
  const content = tabs.find((t) => t.key === active) || tabs[0];

  return (
    <div>
      {/* Heading */}
      <SectionHeading>
        <span className="text-[23px] sm:text-[24px] md:text-[40px] font-semibold">
          Our Services
        </span>
      </SectionHeading>

      {/* Tabs */}
      <div className="mt-8 md:mt-12">
        <nav
          role="tablist"
          aria-label="Our Services"
          className="relative mx-auto w-full border-b border-[var(--border)]"
        >
          <div className="grid grid-cols-2">
            {tabs.map((t) => {
              const isActive = t.key === active;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`services-panel-${t.key}`}
                  onClick={() => setActive(t.key)}
                  className={[
                    "relative flex items-center justify-center rounded-t-2xl transition-colors",
                    "h-12 sm:h-14 md:h-20 px-2 sm:px-4 md:px-6",
                    "text-sm sm:text-base md:text-xl font-medium",
                    isActive
                      ? "tab-active-gradient text-foreground after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[3px] after:rounded-t-full after:bg-[#FDC300]"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="mt-8 md:mt-10 grid grid-cols-1 items-center gap-8 md:grid-cols-2">
        {/* Text Section */}
        <div className="max-w-2xl">
          <h3 className="text-[23px] sm:text-[24px] md:text-5xl tracking-[-0.015em] leading-[1.25]">
            {content.heading}
          </h3>

          <p className="mt-4 md:mt-5 text-muted-foreground leading-relaxed text-[14px] sm:text-[15px] md:text-lg">
            {content.text}
          </p>

          <Link
            href="/services"
            className="hidden md:inline-flex mt-8 items-center rounded-[30px] bg-[#FDC300] px-[18px] py-[9px] text-[14px] font-semibold text-black shadow-sm hover:brightness-95 transition-all"
          >
            Learn More
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Link>
        </div>

        {/* Image Section (FIXED) */}
        <div className="w-full relative">
          <div className="w-full aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-3xl">
            <img
              id={`services-image-${content.key}`}
              src={content.imgSrc || "/placeholder.svg"}
              alt={`${content.heading} image`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Mobile button */}
          <Link
            href="/services"
            className="md:hidden absolute bottom-4 left-4 inline-flex items-center rounded-[30px] bg-[#FDC300] px-[18px] py-[9px] text-[14px] font-semibold text-black shadow-sm hover:brightness-95 transition-all"
          >
            Learn More
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
